import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  Collapse,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { LogSetting, ApiResponse } from '../types';
import logSettingsService from '../services/logSettingsService';

interface LogSettingsProps {
  open: boolean;
  onClose: () => void;
}

const LogSettings: React.FC<LogSettingsProps> = ({ open, onClose }) => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['autenticacao']);
  const [localSettings, setLocalSettings] = useState<LogSetting[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  const queryClient = useQueryClient();

  // Query para buscar configurações
  const {
    data: settingsResponse,
    isPending,
    error
  } = useQuery<ApiResponse<LogSetting[]>>({
    queryKey: ['log-settings'],
    queryFn: () => logSettingsService.getLogSettings(),
    enabled: open
  });

  // Effect para processar dados quando a query é bem-sucedida
  useEffect(() => {
    if (settingsResponse?.data) {
      setLocalSettings([...settingsResponse.data]);
      setHasChanges(false);
    }
  }, [settingsResponse]);

  // Mutation para atualizar configurações
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: LogSetting[]) => {
      const updates = settings.map(setting => ({
        log_type: setting.log_type,
        log_action: setting.log_action,
        enabled: setting.enabled,
        description: setting.description
      }));
      return logSettingsService.updateMultipleLogSettings(updates);
    },
    onSuccess: () => {
      toast.success('Configurações atualizadas com sucesso!');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['log-settings'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar configurações: ${error.message}`);
    }
  });

  // Mutation para resetar configurações
  const resetSettingsMutation = useMutation({
    mutationFn: () => logSettingsService.resetLogSettings(),
    onSuccess: () => {
      toast.success('Configurações resetadas para os valores padrão!');
      setShowResetDialog(false);
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['log-settings'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao resetar configurações: ${error.message}`);
    }
  });

  const settings = settingsResponse?.data || [];
  const groupedSettings = logSettingsService.groupSettingsByType(localSettings.length > 0 ? localSettings : settings);
  const stats = logSettingsService.getSettingsStats(localSettings.length > 0 ? localSettings : settings);
  const importantDisabled = logSettingsService.getImportantDisabledSettings(localSettings.length > 0 ? localSettings : settings);

  const handleToggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleSettingChange = (settingId: number, enabled: boolean) => {
    setLocalSettings(prev => {
      const updated = prev.map(setting =>
        setting.id === settingId ? { ...setting, enabled } : setting
      );
      setHasChanges(true);
      return updated;
    });
  };

  const handleSaveChanges = () => {
    updateSettingsMutation.mutate(localSettings);
  };

  const handleResetToDefaults = () => {
    resetSettingsMutation.mutate();
  };

  const handleDiscardChanges = () => {
    if (settings) {
      setLocalSettings([...settings]);
      setHasChanges(false);
    }
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      'autenticacao': '🔐',
      'cliente': '👤',
      'contrato': '📋',
      'fatura': '💰',
      'sistema': '⚙️'
    };
    return icons[type as keyof typeof icons] || '📝';
  };

  const getTypeColor = (type: string) => {
    const colors = {
      'autenticacao': 'error',
      'cliente': 'primary',
      'contrato': 'info',
      'fatura': 'success',
      'sistema': 'warning'
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  if (error) {
    const errorMessage = (error as any)?.message || 'Erro desconhecido';
    const isNetworkError = errorMessage.includes('Network Error') || errorMessage.includes('ECONNREFUSED');
    const isAuthError = (error as any)?.response?.status === 403;
    
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Configurações de Logs</DialogTitle>
        <DialogContent>
          {isNetworkError ? (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                ❌ Erro de Conexão
              </Typography>
              <Typography variant="body2">
                Não foi possível conectar com o servidor backend. 
                Verifique se o servidor está rodando na porta 3001.
              </Typography>
            </Alert>
          ) : isAuthError ? (
            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                🔒 Acesso Negado
              </Typography>
              <Typography variant="body2">
                Você não tem permissão para acessar as configurações de logs. 
                Faça login com uma conta de administrador.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ Erro Inesperado
              </Typography>
              <Typography variant="body2">
                {errorMessage}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <SettingsIcon fontSize="small" />
            <Typography variant="subtitle1">Configurações de Logs</Typography>
            <Chip
              label={`${stats.enabled}/${stats.total} ativos`}
              color={stats.percentage > 80 ? 'success' : stats.percentage > 50 ? 'warning' : 'error'}
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, px: 2, pb: 1 }}>
          {isPending ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {/* Alertas importantes */}
              {importantDisabled.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2, py: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    ⚠️ Configurações importantes desabilitadas:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {importantDisabled.map(setting => (
                      <Chip
                        key={setting.id}
                        label={`${logSettingsService.formatLogType(setting.log_type)} - ${logSettingsService.formatLogAction(setting.log_action)}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Alert>
              )}

              {/* Estatísticas */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined" sx={{ py: 1 }}>
                    <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="textSecondary" variant="caption" display="block">Total</Typography>
                      <Typography variant="h6">{stats.total}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined" sx={{ py: 1 }}>
                    <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="textSecondary" variant="caption" display="block">Habilitados</Typography>
                      <Typography variant="h6" color="success.main">{stats.enabled}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined" sx={{ py: 1 }}>
                    <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="textSecondary" variant="caption" display="block">Desabilitados</Typography>
                      <Typography variant="h6" color="error.main">{stats.disabled}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined" sx={{ py: 1 }}>
                    <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="textSecondary" variant="caption" display="block">Percentual</Typography>
                      <Typography variant="h6" color={stats.percentage > 80 ? 'success.main' : 'warning.main'}>{stats.percentage}%</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Progresso visual */}
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Configurações ativas
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.enabled} de {stats.total}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.percentage}
                  color={stats.percentage > 80 ? 'success' : stats.percentage > 50 ? 'warning' : 'error'}
                  sx={{ height: 6, borderRadius: 2 }}
                />
              </Box>

              {/* Configurações por categoria */}
              {Object.entries(groupedSettings).map(([type, typeSettings]) => (
                <Paper key={type} variant="outlined" sx={{ mb: 1 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1.5}
                    py={1}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleToggleSection(type)}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2">
                        {getTypeIcon(type)} {logSettingsService.formatLogType(type)}
                      </Typography>
                      <Chip
                        label={`${typeSettings.filter(s => s.enabled).length}/${typeSettings.length} ativos`}
                        color={getTypeColor(type) as any}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <IconButton>
                      {expandedSections.includes(type) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedSections.includes(type)}>
                    <Divider />
                    <Box px={1.5} py={1}>
                      <FormControl component="fieldset" fullWidth>
                        <FormGroup>
                          {typeSettings.map((setting) => (
                            <Box key={setting.id} sx={{ mb: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={setting.enabled}
                                    onChange={(e) => handleSettingChange(setting.id, e.target.checked)}
                                    color={getTypeColor(type) as any}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="caption" fontWeight="medium">
                                      {logSettingsService.formatLogAction(setting.log_action)}
                                    </Typography>
                                    {setting.description && (
                                      <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.7rem' }}>
                                        {setting.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </Box>
                          ))}
                        </FormGroup>
                      </FormControl>
                    </Box>
                  </Collapse>
                </Paper>
              ))}

              {/* Informações adicionais */}
              <Alert severity="info" sx={{ mt: 2, py: 1 }}>
                <Typography variant="body2">
                  <InfoIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  <strong>Importante:</strong> Logs desabilitados não serão registrados no sistema. 
                  Configurações de autenticação e financeiro são especialmente importantes para auditoria.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => setShowResetDialog(true)}
            disabled={updateSettingsMutation.isPending || resetSettingsMutation.isPending}
            startIcon={<RefreshIcon />}
          >
            Resetar
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          {hasChanges && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleDiscardChanges}
              disabled={updateSettingsMutation.isPending}
            >
              Descartar
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleSaveChanges}
            disabled={!hasChanges || updateSettingsMutation.isPending}
            startIcon={updateSettingsMutation.isPending ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {updateSettingsMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>

          <Button size="small" onClick={onClose} disabled={updateSettingsMutation.isPending || resetSettingsMutation.isPending}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação para reset */}
      <Dialog open={showResetDialog} onClose={() => setShowResetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" fontSize="small" />
            <Typography variant="subtitle1">Resetar Configurações</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2">
            Tem certeza que deseja resetar todas as configurações para os valores padrão?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button size="small" onClick={() => setShowResetDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleResetToDefaults}
            disabled={resetSettingsMutation.isPending}
            startIcon={resetSettingsMutation.isPending ? <CircularProgress size={18} /> : <RefreshIcon />}
          >
            {resetSettingsMutation.isPending ? 'Resetando...' : 'Resetar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LogSettings; 